import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../selectors/menu';
import { selectFinalOrderSubmitted } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectIsCarPresent } from '../../selectors/message';
import { useReminder } from '../../contexts/ReminderContext';
import Colors from '../../utils/color';

/**
 * PULSE SIGNALS (HITL 3.0) — a chip row of live order signals in the Pulse strip.
 *
 * Two chip kinds, each shown only when active (no heading, no empty-state — an
 * empty row simply renders nothing so it takes no space):
 *   • Express mode (amber) — express mode ON by either trigger
 *     (`isExpressUpsellMode || isExpressTimerActive`), dropped on car exit.
 *   • Reminder (violet) — the live slash-command reminder (`useReminder`), folded
 *     in from the old Action Center. A deliberately distinct colour from the
 *     AI-suggestion box so it doesn't read as the ask.
 *
 * More signals (intent, upsell state, …) can slot into the same row later.
 * Gated by hitl_3_0 at the call site (CustomerPulse).
 */
const useStyles = makeStyles((theme: Theme) => ({
  row: {
    marginTop: theme.spacing(1.25),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
  },
  // Express signal — amber pill.
  sigchip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.375, 1, 0.375, 0.875),
    borderRadius: 999,
    backgroundColor: '#fff7e0',
    border: '1px solid #ffe19a',
    color: '#8a5d00',
    fontSize: '0.68rem',
    fontWeight: 600,
  },
  sigIcon: {
    fontSize: '0.85rem',
    color: Colors.amber,
    flexShrink: 0,
  },
  // Reminder — violet chip, distinct from both the amber signal and the
  // (blue-lavender) AI-suggestion box. Wraps for long reminders.
  remindChip: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1.125, 0.5, 0.875),
    borderRadius: 12,
    backgroundColor: '#f0e9fb',
    border: '1px solid #d6c4ef',
    color: '#6a34ac',
    fontSize: '0.71rem',
    fontWeight: 500,
    lineHeight: 1.3,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  remindIcon: {
    fontSize: '0.85rem',
    color: '#7c4dc4',
    flexShrink: 0,
    marginTop: 1,
  },
}));

const PulseSignals = () => {
  const classes = useStyles();
  const isExpressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const isExpressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const { reminderMessage, isVisible: reminderVisible } = useReminder();

  // The Express chip is a per-order signal: only show it while a car is present
  // and the order is still live. Hide it once the car has left or the order is
  // finished/cancelled, so it never lingers past the interaction.
  const expressActive =
    (isExpressUpsellMode || isExpressTimerActive) &&
    isCarPresent &&
    !finalOrderSubmitted;
  const showReminder = reminderVisible && !!reminderMessage;

  // Empty row takes no space (no heading / placeholder).
  if (!expressActive && !showReminder) {
    return null;
  }

  return (
    <div className={classes.row} data-testid="pulse-signals">
      {expressActive && (
        <span className={classes.sigchip} data-testid="signal-express-mode">
          <BoltIcon className={classes.sigIcon} />
          Express
        </span>
      )}
      {showReminder && (
        <span className={classes.remindChip} data-testid="signal-reminder">
          <NotificationsNoneOutlinedIcon className={classes.remindIcon} />
          <span>{reminderMessage}</span>
        </span>
      )}
    </div>
  );
};

export default PulseSignals;
